import URI from 'urijs'
import _ from 'underscore'
import Backbone from '@polleverywhere/backbone'
import env from '@polleverywhere/core/lib/env'
import { params as getParams } from '@polleverywhere/core/lib/util'
import { displaySetByDefault as displayControlSetByDefault } from './control_permissions'

const POLL_OPTIONS = [
  'archive_id',
  'controls',
  'push',
  'grade',
  'regions',
  'service_area_id',
  'allow_fullscreen',
  'show_total',
  'background',
  'short_poll',
  'animation',
  'log_level',
  'timer',
  'platform'
]

const DATA_OPTIONS = [
  'url', // deprecated
  'api_url',
  'firehose_ssl',
  'firehose_url',
  'active_tour'
]

const FORCE_OPTIONS = [
  'no_title', // Deprecated. Use `hide=title` instead.
  'no_instructions', // Deprecated. Use `hide=instructions` instead.
  'not_live', // Deprecated. Use `display=static` instead.
  'fullscreen', // Activate the poll upon a successful login, and otherwise mimic fullscreen behavior
  'qrcode' // Show the presenter's QR code when the poll is loaded if the presenter has the correct feature flag
]

const HIDE_OPTIONS = [
  'title', // poll title
  'instructions', // voting instructions
  'upsells',
  'signup',
  'empty_ftp_notice',
  'correctness',
  'clickable_image_regions',
  'keywords',
  'controls', // this will render but visually hide controls unlike ?controls=none
  'activate_tooltip',
  'steps', // this will hide join instruction steps
  'footer', // hides upsell / connection warning footer,
  'brand' // hides branding (associated model attribute is `show_branding`)
]

const DISPLAY_OPTIONS = [
  'static', // This is the old force=not_live param.  Don't poll/connect to firehose for live updates.
  'debug',
  'wrong_user_login', // deprecated
  'user_login',
  'placeholder_steps', // placeholder steps for join instructions
  'web', // join instructions shows web modality
  'sms', // join instructions shows sms modality

  // Forces an MCP with `display_state=chart` to show correctness.
  'correctness'
]

const DISPLAY_STATE_OPTIONS = [
  'title', // title screen
  'instructions', // instructions view
  'chart',
  'welcome',
  'leaderboard',
  'finale', // leaderboard with confetti
  'join_instructions' // how to respond on your device
]

// Available actions for viz to take when a poll becomes visible
// during a presentation in slideware
const ONSCREEN_OPTIONS = [
  'persist'
]

function normalizeParamArrays (param = []) {
  try {
    if (Array.isArray(param)) {
      return _.compact(param)
    } else if (typeof param === 'string') {
      return _.compact(param.split(','))
    } else {
      return []
    }
  } catch (e) {
    return []
  }
}

export default class Settings extends Backbone.Model {
  constructor (...args) {
    super(...args)
    this.toggleAnimation = this.toggleAnimation.bind(this)
    this.configureEnv = this.configureEnv.bind(this)
    this.hasChartNavigation = this.hasChartNavigation.bind(this)
    this.autoStartTimer = this.autoStartTimer.bind(this)
    this.isTouchEnabled = this.isTouchEnabled.bind(this)
    this.stringToBoolean = this.stringToBoolean.bind(this)
    this.isDebugMode = this.isDebugMode.bind(this)
    this.whenOnscreen = this.whenOnscreen.bind(this)
    this.displays = this.displays.bind(this)
    this.hides = this.hides.bind(this)
    this.forces = this.forces.bind(this)
    this.hasLockedControls = this.hasLockedControls.bind(this)
    this.hasFixedControls = this.hasFixedControls.bind(this)
    this.reset = this.reset.bind(this)
    this.defaults = this.defaults.bind(this)
  }

  initialize (opts) {
    if (opts == null) {
      opts = {}
    }
    this.on('change:animation', this.toggleAnimation)
    this.on('change:url', this.configureEnv)
    this.on('change:firehose_ssl', this.configureEnv)
    this.on('change:firehose_url', this.configureEnv)

    this.toggleAnimation()
    this.configureEnv()
  }

  defaults () {
    // TODO: clean up the query string overrides with something like
    // ?force=foo,bar instead of ?foo=true&bar=true

    return {
      controls: 'limited',
      push: 'manual',
      grade: false,
      regions: false,
      service_area_id: -1,

      allow_fullscreen: true,
      short_poll: false,
      animation: true,

      // New way of toggling things.
      // If you are adding new flags, use tokens in the appropriate param
      // instead of individual params like above.  Remember to avoid negative
      // params

      force: [],

      hide: [],

      display: [],

      onscreen: []
    }
  }

  reset (params = {}) {
    if (_.isString(params)) {
      params = getParams(params)
    }

    // Old whitelist
    let options = _.pick(params, POLL_OPTIONS)
    options = _.defaults(options, this.defaults())

    // TODO: remove this when simple_keywords is removed from the iframe url in rails
    if (options.controls === 'simple_keywords') {
      options.controls = 'editor'
    }

    // New tokens that force specific behaviors
    options.force = _.intersection(FORCE_OPTIONS, normalizeParamArrays(params.force))
    options.hide = _.intersection(HIDE_OPTIONS, normalizeParamArrays(params.hide))
    options.display = _.intersection(DISPLAY_OPTIONS, normalizeParamArrays(params.display))
    options.onscreen = _.intersection(ONSCREEN_OPTIONS, normalizeParamArrays(params.onscreen))

    if (DISPLAY_STATE_OPTIONS.includes(params.display_state)) {
      options.display_state = params.display_state
    } else if (options.display.includes('chart') || options.force.includes('chart')) {
      options.display_state = 'chart'
    } else {
      options.display_state = undefined
    }

    // Save activity_state
    options.activity_state = params.activity_state

    // Data source options
    _.extend(options, _.pick(params, DATA_OPTIONS))

    // Convert "true" to true
    options = _.mapObject(options, this.stringToBoolean)

    // Commit the changes

    // These 2 override default model values so unset them to
    // honor original model values
    this.unset('archive_id')
    this.unset('background')

    this.set(options)
  }

  // These controls will always be displayed unless in fullscreen
  // where mouse movement/touch will show/hide them
  // Padding will be applied for them not to overlap the chart
  hasFixedControls () {
    return displayControlSetByDefault(this.get('controls'))
  }

  // These controls don't show/hide on mouse movement or touch
  // they will likely be controlled by the API
  hasLockedControls () {
    return /office|webpresenter/.test(this.get('controls'))
  }

  // poll-renderer passes in animation=false
  toggleAnimation () {
    Backbone.$.fx.off = !this.get('animation')
  }

  configureEnv () {
    const settings = {}

    // handle deprecated param
    if (this.has('url')) {
      this.set({ api_url: this.get('url') })
    }

    if (this.has('api_url')) {
      settings.api_url = URI(this.get('api_url')).href()
    }

    if (this.has('firehose_url')) {
      settings.firehose_url = URI(this.get('firehose_url')).href()
    }

    if (this.has('firehose_ssl')) {
      settings.firehose_ssl = this.get('firehose_ssl')
    }

    if (this.has('active_tour')) {
      settings.active_tour = this.get('active_tour')
    }

    _.defaults(settings, {
      api_url: env.get('api_url'),
      firehose_url: env.get('firehose_url'),
      firehose_ssl: env.get('firehose_ssl'),
      active_tour: false
    })

    env.set(settings)

    // Force a file extension at the end of each request so that
    // we can recieved JSON data over CORS connections that don't
    // support headers.
    // return (PollEv.Ajax.Defaults.forceSuffix = true);
  }

  forces (val) {
    return _.contains(this.get('force'), val)
  }

  hides (val) {
    return _.contains(this.get('hide'), val) || this.forces(`no_${val}`)
  }

  displays (val) {
    const forcesParam = val === 'static' ? 'not_live' : val
    return _.contains(this.get('display'), val) || this.forces(forcesParam)
  }

  whenOnscreen (val) {
    return _.contains(this.get('onscreen'), val)
  }

  isDebugMode () {
    return this.get('log_level') === 'debug'
  }

  autoStartTimer () {
    return this.get('timer') === 'auto-start'
  }

  isTouchEnabled () {
    return Modernizr.touch || Modernizr.touchevents
  }

  hasChartNavigation () {
    return /editor|fullscreen|all/.test(this.get('controls'))
  }

  stringToBoolean (value) {
    if (_.isString(value)) {
      if (/true|false/.test(value)) {
        // convert to boolean from string
        return value.toLowerCase() === 'true'
      } else {
        return value.toLowerCase()
      }
    } else {
      return value
    }
  }
}
